import * as RadixPopover from '@radix-ui/react-popover'
import { useAction, useAtom } from '@reatom/npm-react'
import { useState } from 'react'
import styled from 'styled-components'

import { Link, QuestionIcon, Tooltip, Typography } from '@blade/ui-kit'
import { Numeric } from '@blade/ui-kit'
import { isLoggedInAtom } from '@root/entities/auth'
import { unsafeUrlDialogEvent } from '@root/entities/deeplinking/unsafe-url-dialog'
import { normalizeTelegramStringHref } from '@root/entities/socials/model/socials-normalize'
import { useWebSignIn } from '@root/features/auth/web-sign-in'
import { showReportTelegramGroupDialogAction } from '@root/features/user/report/atoms/reatom-report-telegram-group'
import { useTranslation } from '@root/shared/localization'
import {
  ProfileIcon,
  TelegramSocialIcon,
  WarningFilledIcon,
} from '@root/shared/ui-kit'

import type { Socials } from '@root/entities/socials'
import type { ChildrenProps, ClassNameProps } from '@root/shared/react'
import type { DialogControlProps } from '@root/shared/ui-kit'

type TelegramLinksProps = {
  socials: Socials.Socials
  size: number
  variant?: 'default' | 'detailed'
} & ClassNameProps

export const TelegramLinks: React.FC<TelegramLinksProps> = ({
  className,
  socials,
  size,
  variant = 'default',
}) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const openUnsafeUrlDialog = useAction(unsafeUrlDialogEvent)

  if (!socials.telegram) {
    return undefined
  }

  const handleClick = (
    e: React.MouseEvent<HTMLButtonElement>,
    href: string | URL,
  ) => {
    e.stopPropagation()
    e.preventDefault()

    openUnsafeUrlDialog([href, 'telegram'])
  }

  const { telegram } = socials
  if (typeof telegram === 'string') {
    const telegramHref = normalizeTelegramStringHref(telegram)
    return (
      <Tooltip content={telegram}>
        <SocialLink
          data-variant={variant}
          onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
            handleClick(e, telegramHref)
          }
          target="_blank"
          href={telegramHref}
        >
          <TelegramSocialIcon size={size} />
          {variant === 'detailed' ? t('common.telegram') : undefined}
        </SocialLink>
      </Tooltip>
    )
  }

  if (telegram.length === 0) {
    return undefined
  }

  return (
    <>
      <Popup open={open} onOpenChange={setOpen} links={telegram}>
        <Root className={className} data-variant={variant}>
          <TelegramSocialIcon size={13} />
          <QuestionIcon size={12} />
        </Root>
      </Popup>
    </>
  )
}

const Root = styled.div`
  all: unset;
  height: 16px;
  border-radius: 6px;
  color: var(--text-11);
  padding-inline: 4px;

  display: grid;
  align-items: center;
  grid-auto-flow: column;
  gap: 1px;
  cursor: pointer;

  transition: background 0.125s ease-in-out;

  &:hover {
    background: var(--background-3);
  }

  &[data-variant='detailed'] {
    border-radius: 6px;
    background: rgba(var(--background-1-rgb), 0.5);

    &:hover {
      background: rgba(var(--background-1-rgb), 0.9);
    }
  }
`

const Popup = ({
  links,
  children,
  open,
  onOpenChange,
}: { links: Array<Socials.TgInfo> } & ChildrenProps & DialogControlProps) => {
  const openUnsafeUrlDialog = useAction(unsafeUrlDialogEvent)
  const report = useAction(showReportTelegramGroupDialogAction)
  const signIn = useWebSignIn()
  const [isLoggedIn] = useAtom(isLoggedInAtom)

  const renderLink = (link: Socials.TgInfo) => {
    const handleClick = (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation()
      e.preventDefault()

      openUnsafeUrlDialog([link.portalLink, 'telegram'])
    }
    const handleReport = (e: React.MouseEvent<unknown>) => {
      e.stopPropagation()
      e.preventDefault()
      if (!isLoggedIn) {
        signIn()
        return
      }
      report(link)
    }
    return (
      <Item
        key={link.portalLink}
        onClick={(e) => {
          e.stopPropagation()
        }}
      >
        <ItemLink onClick={handleClick} ellipsis>
          {link.name}
        </ItemLink>
        <ItemMembers>
          <ProfileIcon size={14} />
          <span>
            <Numeric value={link.members} preset="currency-amount-short" />
          </span>
        </ItemMembers>
        <Separator />
        <WarningIcon onClick={handleReport} size={12} />
      </Item>
    )
  }
  return (
    <>
      <RadixPopover.Root open={open} onOpenChange={onOpenChange}>
        <RadixPopover.Trigger
          onClick={(e) => {
            e.stopPropagation()
            onOpenChange?.(true)
          }}
          asChild
        >
          {children}
        </RadixPopover.Trigger>
        <RadixPopover.Portal>
          <Content>{links.map(renderLink)}</Content>
        </RadixPopover.Portal>
      </RadixPopover.Root>
    </>
  )
}

const Content = styled(RadixPopover.Content)`
  width: 170px;
  border-radius: 6px;
  background: var(--background-2);
  box-shadow: 0 16px 20px 0 var(--appearanceA-6);
  padding: 3px;

  display: grid;
  gap: 3px;
`

const Item = styled.div`
  border-radius: 4px;
  height: 20px;
  background: var(--appearanceA-5);

  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto auto;
  align-items: center;
  padding-inline: 8px 4px;
`

const ItemLink = styled(Typography)`
  color: var(--primary-10);
  font-size: 11px;
  font-weight: 500;
  line-height: 12px;
  cursor: pointer;
`

const ItemMembers = styled.div`
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  gap: 2px;

  color: var(--text-11);
  font-size: 11px;
  font-weight: 400;
  line-height: 12px;
  margin-inline: 4px 4px;
`

const Separator = styled.div`
  height: 100%;
  width: 1px;
  background: var(--background-2);
`

const WarningIcon = styled(WarningFilledIcon)`
  color: var(--down-8);
  margin-left: 4px;
  cursor: pointer;
`

const SocialLink = styled(Link)`
  &[data-variant='detailed'] {
    color: var(--textA-11);

    border-radius: 6px;
    background: rgba(11, 11, 11, 0.5);
    backdrop-filter: blur(3px);
    padding: 3px 6px;

    display: grid;
    grid-auto-flow: column;
    gap: 1px;
    align-items: center;
    justify-content: center;

    font-size: 10px;
    transition: background 0.2s ease-in-out;

    &:hover {
      background: rgba(11, 11, 11, 0.7);
    }
  }
`
